import { useEffect, useState } from 'react';
import type { LineItem } from 'tsconfig/types';

/**
 * Checks if a line item has a bulk deal discount applied
 * @param item - The line item to check for bulk deal discount
 * @param discountName - The name of the discount to search for
 * @returns Boolean indicating if the item has specified bulk deal discount
 *
 * @example
 * ```typescript
 * const hasDiscount = hasBulkDealDiscount(lineItem, "bulk10");
 * Returns true if lineItem has a "bulk10" discount applied
 * ```
 */
export const hasBulkDealDiscount = (item: LineItem | undefined, discountName: string): boolean =>
  item?.discountedPricePerQuantity?.some(dpq =>
    dpq?.discountedPrice?.includedDiscounts?.some(discount =>
      discount?.discount?.name?.toLowerCase().includes(discountName.toLocaleLowerCase()),
    ),
  ) ?? false;

type SavingsSummary = { totalSavings: number; unlimitedSavings: number; upsellSavings: number };

const isPresellOrPreRelease = (item: LineItem) =>
  item?.product?.skus?.some(sku => sku?.preSellItem || sku?.preReleaseItem);

const calculateItemTotalSavings = (item: LineItem): number => {
  const baseSavings = item?.savings?.totalCartDiscountSavings || 0;
  const additionalSavings = item?.savings?.totalProductDiscountSavings || 0;
  return isPresellOrPreRelease(item) ? baseSavings : baseSavings + additionalSavings;
};

const calculateUpsellSavings = (item: LineItem): number => {
  if (!item?.discountedPricePerQuantity) {
    return 0;
  }

  return item.discountedPricePerQuantity.reduce((sum, dpq) => {
    const includedDiscounts = dpq?.discountedPrice?.includedDiscounts || [];
    const totalDiscount = includedDiscounts.reduce((discountSum, discountEntry) => {
      const discountName = discountEntry?.discount?.name?.toLowerCase();
      if (discountName?.includes('subscribesave')) {
        return discountSum + (discountEntry?.discountedAmount?.amount || 0);
      }
      return discountSum;
    }, 0);

    return sum + totalDiscount * (dpq?.quantity || 1);
  }, 0);
};

export const calculateOrderSummarySavings = (lineItems: LineItem[]): SavingsSummary => {
  let totalSavings = 0;
  let unlimitedSavings = 0;
  let upsellSavings = 0;

  for (const item of lineItems || []) {
    if (!item?.itemClassifications?.length) {
      totalSavings += calculateItemTotalSavings(item);
    }

    if (item?.unitPrice?.priceType === 'mpp') {
      unlimitedSavings += item?.savings?.totalProductDiscountSavings || 0;
    }

    upsellSavings += calculateUpsellSavings(item);
  }

  return {
    totalSavings: Math.round((totalSavings - unlimitedSavings - upsellSavings) * 100) / 100,
    unlimitedSavings: Math.round(unlimitedSavings * 100) / 100,
    upsellSavings: Math.round(upsellSavings * 100) / 100,
  };
};

const stickHeader = '.sticky-header.active';
export function waitForStickyHeader(): Promise<HTMLElement> {
  return new Promise(resolve => {
    const existing = document.querySelector(stickHeader);
    if (existing instanceof HTMLElement) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const sticky = document.querySelector(stickHeader);
      if (sticky instanceof HTMLElement) {
        observer.disconnect();
        resolve(sticky);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  });
}

export function useStickyHeaderHeight(): number | null {
  const [headerHeight, setHeaderHeight] = useState<number | null>(114);

  useEffect(() => {
    const headerElement = document.querySelector(stickHeader);
    if (headerElement instanceof HTMLElement) {
      setHeaderHeight(headerElement.offsetHeight);
    } else {
      waitForStickyHeader().then(sticky => {
        setHeaderHeight(sticky.offsetHeight);
      });
    }
  }, []);
  return headerHeight;
}

/**
 * Checks if any of the provided line items correspond to an "unlimited" item
 * by evaluating their scheme ID.
 *
 * @param {LineItem[]} lineItems - An array of line items to evaluate.
 * @returns {boolean} `true` if an unlimited item is found; otherwise, `false`.
 */
export const checkIsUnlimitedItem = (lineItems: LineItem[]): boolean =>
  lineItems.some(({ sku, product }) => {
    const schemeId =
      sku?.schemeDetails?.[0]?.schemeId?.toLowerCase() || product?.schemeDetails?.schemeId?.toLowerCase();
    return schemeId ? /ul|unl|unt|unlt/.test(schemeId) : false;
  });
